import { take, put, all, takeEvery, race, call, select } from "redux-saga/effects";
import * as UnitStore from './unitStore';
import { actionCreators as actions } from './unitStore'
import { AddUnitModel, getEmptyUnitModel, UnitModel } from "../UnitModels";
import * as unitsApi from '../../api/unitsApi'
import { ApplicationState } from "../../store";
import * as routeApi from "../../api/routesApi";
import { LoginState } from "../../login/loginStore";
import { momentToString, momentToDtoString } from "../../utils/dateUtilities";
import { actions as rrfActions } from 'react-redux-form';
import { FilterInfo, RouteModel, LocationModel } from "../../common/models/CommonModels";
import { sendUnitInformationEmailEvent, sendUnitInformationToReporterEmailEvent } from '../../utils/gaUtils';

export function* watchUnitSagas() {
    yield all([
        takeEvery(UnitStore.ActionKeys.AddNewFreeUnit, addNewFreeUnit),
        takeEvery(UnitStore.ActionKeys.RequestFreeUnits, fetchFreeUnits),
        takeEvery(UnitStore.ActionKeys.DeleteUnit, deleteUnitFlow),
        takeEvery(UnitStore.ActionKeys.CopyUnit, copyUnitFlow),
        takeEvery(UnitStore.ActionKeys.SendEmail, sendEmailFlow),
        takeEvery(UnitStore.ActionKeys.SendEmailToReporter, sendEmailToReporterFlow),
        takeEvery(UnitStore.ActionKeys.ResetAddNewUnitFormTrailerIdentifier, resetAddNewUnitFormTrailerIdentifier)
    ])
}


// Store selectors
const getUserData = (state: ApplicationState) => state.login;
const getFilters = (state: ApplicationState) => state.units.filters;


function* resetAddNewUnitFormTrailerIdentifier() {
    yield put(rrfActions.change("newUnit.trailerIdentifier", ""));
}

export function* fetchFreeUnits(action: UnitStore.FreeUnitsRequestAction) {
    const unitList: UnitModel[] = yield call(loadUnits, action.filters);

    if (action.includeRoutes && unitList.length > 0) {
        // Fetch routes for units
        const unitsGrouping = new Map<string, UnitModel[]>();
        unitList.forEach(unit => {
            if (unit.destinationCity) {
                const unitsGroup = unitsGrouping.get(unit.departureCity.id + unit.destinationCity.id);
                if (!unitsGroup) {
                    unitsGrouping.set(unit.departureCity.id + unit.destinationCity.id, [unit]);
                }
                else {
                    unitsGroup.push(unit);
                }
            }
        })

        yield all(Array.from(unitsGrouping.values()).map(units => fetchUnitsRoute(units, units[0].departureCity.id, units[0].destinationCity.id)));
    }
}

function* fetchUnitsRoute(units: UnitModel[], fromId: string, toId: string) {
    const routeResult: routeApi.RouteResult | undefined = yield call(routeApi.getRoute, fromId, toId);

    if (routeResult) {
        const routes: RouteModel[] = units.map(unit => routeApi.getRouteModel(unit.id, unit.departureCity, unit.destinationCity, routeResult));
        yield put(actions.receivedUnitRoutes(routes));
    }
}

function* loadUnits(filters: FilterInfo) {
    const units: UnitModel[] = yield call(unitsApi.getUnits, filters);

    const { userId, companyId } = yield select(getUserData);

    // Set flags for personal or company reports.
    let unitList = units.map(unit => Object.assign({}, unit, { isOwn: userId === unit.createdBy, isOwnCompany: companyId === unit.companyId }));

    if (filters.filterMode === "own") {
        unitList = unitList.filter(item => item.isOwn);
    }
    else if (filters.filterMode === "company") {
        unitList = unitList.filter(item => item.isOwnCompany);
    }
    else {
        unitList = unitList.filter(item => !item.deletedAt);
    }

    yield put(UnitStore.actionCreators.receivedFreeUnits(unitList));
    return unitList;
}

function* addNewFreeUnit(action: UnitStore.AddEditUnitAction): IterableIterator<any> {

    let editMode = false;
    if (action.unit) {
        // Load initial state for add edit form model.
        yield put(rrfActions.load("newUnit", action.unit));
        editMode = true;
    }

    // This action will inform UI to show add dialog.
    yield put(actions.addOrEditUnitStarted(action.unit));

    // Wait user input to submit or cancel adding.
    const { submit, cancel } : any = yield race({
        submit: take(UnitStore.ActionKeys.AddNewFormSubmitted),
        cancel: take(UnitStore.ActionKeys.AddNewFormCancelled)
    });

    if (submit) {
        const unitData: AddUnitModel = submit.data;

        const userData : any = yield select(getUserData);

        if (userData && userData.token) {

            const injected = Object.assign({}, unitData, {
                readyTime: momentToDtoString(unitData.readyTime),
                deliveryTime: momentToDtoString(unitData.deliveryTime),
                createdBy: unitData.createdBy || userData.token.sub,
                companyId: unitData.companyId || userData.companyId,
                recurrence: {
                    ...unitData.recurrence,
                    startDate: unitData.recurrence.startDate ?
                        momentToDtoString(unitData.recurrence.startDate) : unitData.recurrence.startDate,
                    endDate: unitData.recurrence.endDate ?
                        momentToDtoString(unitData.recurrence.endDate) : unitData.recurrence.endDate
                }
            });

            try {
                if (!editMode) {
                    const response = yield call(unitsApi.addUnit, injected);
                    
                    if (response) {
                        const routeResult: routeApi.RouteResult | undefined = yield call(routeApi.getRoute, injected.departureCity.id, injected.destinationCity.id);
                        if (routeResult) {
                            const route = routeApi.getRouteModel(injected.id, injected.departureCity as LocationModel, injected.destinationCity as LocationModel, routeResult);
                            yield put(actions.receivedUnitRoutes([route]));
                        }
                    }
                }
                else {
                    const response = yield call(unitsApi.editUnit, injected);
                    
                    if (response) {
                        const routeResult: routeApi.RouteResult | undefined = yield call(routeApi.getRoute, injected.departureCity.id, injected.destinationCity.id);
                        if (routeResult) {
                            const route = routeApi.getRouteModel(injected.id, injected.departureCity as LocationModel, injected.destinationCity as LocationModel, routeResult);
                            yield put(actions.receivedUnitRoutes([route]));
                        }
                    }
                }
            }
            catch (error) {
            }
        }
    }

    yield put(actions.addOrEditUnitSuccess());
    yield put(rrfActions.load("newUnit", getEmptyUnitModel()));

    if (action.afterAddOrEdit) {
        action.afterAddOrEdit();
    }

    // Reload units using last used filters.
    if (action.reloadUnits) {
        const filters = yield select(getFilters);
        yield call(loadUnits, filters);
    }
}

function* deleteUnitFlow(action: UnitStore.DeleteUnitAction): IterableIterator<any> {

    // Inform UI to show the dialog.
    yield put(UnitStore.actionCreators.deleteStarted(action.unit));

    // Wait for appropriate data.
    const winner: any = yield race({
        submit: take(UnitStore.ActionKeys.DeleteSubmitted),
        cancel: take(UnitStore.ActionKeys.DeleteCancelled)
    });

    // If submit was called, call delete resource api
    if (winner.submit) {
        try {
            const inject = Object.assign({}, winner.submit.data, { id: action.unit.id })  
                yield call(unitsApi.deleteUnit, inject);
        } catch (error) {
            //  yield put(UnitStore.actionCreators.deleteError(error))
        }
    }

    yield put(UnitStore.actionCreators.deleteSuccess());
    yield put(rrfActions.reset("deleteUnit"))

    if (action.afterDelete) {
        action.afterDelete();
    }

    // Reload units using last used filters.
    const filters = yield select(getFilters);
    yield call(loadUnits, filters);
}

function* copyUnitFlow(action: UnitStore.CopyAction) {
    // Create copy of the unit without id.
    const copy = Object.assign({}, action.unit, { id: "" });

    // Load form store with copy values.
    yield put(rrfActions.load("newUnit", copy));

    // Inform UI to show edit dialog.
    yield put(UnitStore.actionCreators.copyUnitStarted(copy));

    const { submit } = yield race({
        submit: take(UnitStore.ActionKeys.AddNewFormSubmitted),
        cancel: take(UnitStore.ActionKeys.AddNewFormCancelled)
    });

    if (submit) {

        try {
            const unitData: AddUnitModel = submit.data;

            const userData: LoginState = yield select(getUserData);

            if (userData && userData.token) {

                const injected = Object.assign({}, unitData, { readyTime: momentToString(unitData.readyTime), deliveryTime: momentToString(unitData.deliveryTime), createdBy: userData.token.sub, companyId: userData.companyId });

                const response = yield call(unitsApi.addUnit, submit.data)
            }
        }
        catch (error) {
            yield put({ type: "COMMON/REQUEST_ERROR", error: { error } });
        }
    }

    yield put(UnitStore.actionCreators.copyUnitSuccess());

    // Reset form store.
    yield put(rrfActions.load("newUnit", getEmptyUnitModel()));

    // Reload units using last used filters.
    const filters = yield select(getFilters);
    yield call(loadUnits, filters);
}

function* sendEmailFlow(action: UnitStore.SendEmailAction) {
    // Inform UI to show email dialog.
    yield put(UnitStore.actionCreators.sendEmailStarted(action.unit));
    const { submit } = yield race({
        submit: take(UnitStore.ActionKeys.SendEmailSubmitted),
        cancel: take(UnitStore.ActionKeys.SendEmailCancelled)
    });

    if (submit) {
        try {
            const succeeded = yield call(unitsApi.sendEmail, action.unit.id, submit.data.message, submit.data.emailAddress);
            if (succeeded) {
                yield put(UnitStore.actionCreators.sendEmailSuccess());
                yield put(rrfActions.reset("infoEmail"));
                sendUnitInformationEmailEvent();
            } else {
                yield put(UnitStore.actionCreators.sendEmailError());
            }
        }
        catch (error) {
            yield put({ type: "COMMON/REQUEST_ERROR", error: { error } });
        }
    }
    else {
        yield put(UnitStore.actionCreators.sendEmailCancelled());
        yield put(rrfActions.reset("infoEmail"));
    }
}

function* sendEmailToReporterFlow(action: UnitStore.SendEmailToReporterAction) {
    // Inform UI to show email dialog.
    yield put(UnitStore.actionCreators.sendEmailToReporterStarted(action.unit));
    const { submit } = yield race({
        submit: take(UnitStore.ActionKeys.SendEmailToReporterSubmitted),
        cancel: take(UnitStore.ActionKeys.SendEmailToReporterCancelled)
    });

    if (submit) {
        try {
            const succeeded = yield call(unitsApi.sendEmailToReporter, action.unit.id, submit.data.message);
            if (succeeded) {
                yield put(UnitStore.actionCreators.sendEmailToReporterSuccess());
                yield put(rrfActions.reset("infoEmail"));
                sendUnitInformationToReporterEmailEvent();
            } else {
                yield put(UnitStore.actionCreators.sendEmailToReporterError());
            }
        }
        catch (error) {
            yield put({ type: "COMMON/REQUEST_ERROR", error: { error } });
        }
    }
    else {
        yield put(UnitStore.actionCreators.sendEmailToReporterCancelled());
        yield put(rrfActions.reset("infoEmail"));
    }
}


// WEBPACK FOOTER //
// ./src/units/store/sagas.ts